import {
  CalendarIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn, getUserLocale, useOnClickOutside } from '@jobcost-monorepo/utils';
import dayjs from '@jobcost-monorepo/dayjs-config';
import React, { useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonProps,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../atoms';

interface Props extends ButtonProps {
  name: string;
  label: string;
  errorName: string;
  inputWidth?: string;
  width?: string;
  minDate?: Date;
  onChangeDate?: (date: Date) => boolean;
  isCalendarOpen?: boolean;
  setIsCalendarOpen?: (val: boolean) => void;
}

export const DatePicker = ({
  name,
  label,
  errorName,
  inputWidth = 'full',
  width = 'w-full',
  minDate,
  onChangeDate,
  isCalendarOpen,
  setIsCalendarOpen,
  ...rest
}: Props) => {
  const { t } = useTranslation('errors');
  const { control, clearErrors } = useFormContext();
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name });

  const error = errors?.[errorName]?.message ?? '';
  const errorTranslated = t(error as string);

  const divRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  if (isCalendarOpen === undefined) isCalendarOpen = isOpen;
  if (setIsCalendarOpen === undefined) setIsCalendarOpen = setIsOpen;

  const [formattedDate, setFormattedDate] = useState<string>('');

  const handleSelectDate = (date?: Date) => {
    if (!!errors) clearErrors(name);
    if (!date) return setIsCalendarOpen(false);
    if (onChangeDate?.(date)) return;
    // non si deve convertire la data con utc dato che è già utc ma la vede con fuso diverso
    onChange(dayjs(date).format('YYYY-MM-DD'));
    setIsCalendarOpen(false);
  };

  useOnClickOutside(divRef, () => {
    setIsCalendarOpen(false);
  });

  const handleClearValue = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setFormattedDate('');
    onChange(undefined);
  };

  const getIcon = () => {
    if (errorTranslated) {
      return (
        <ExclamationCircleIcon
          className="h-5 w-5 text-red"
          aria-hidden="true"
        />
      );
    }
    if (formattedDate === '') {
      return <CalendarIcon className="justify-end h-5 w-5" />;
    }
    return (
      <XMarkIcon
        className="h-5 w-5 text-gray-400"
        onClick={(e) => handleClearValue(e)}
      />
    );
  };

  useEffect(() => {
    return value
      ? setFormattedDate(dayjs.utc(value).format('DD/MM/YYYY'))
      : setFormattedDate('');
  }, [value]);

  return (
    <div ref={divRef} className={cn('relative', width)}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          {label}
        </label>
      )}
      <div className="mt-1 rounded-md">
        <Popover open={isCalendarOpen}>
          <div className="relative">
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'flex justify-between border border-purple70 py-2 px-3 w-full rounded-md shadow-sm duration-0 dark:bg-grey dark:border-purple dark:focus:bg-grey dark:focus:outline-darkPurple',
                  inputWidth === 'full' ? 'w-full' : `w-${inputWidth}`,
                  errorTranslated && 'border-red text-red dark:border-red',
                )}
                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                {...rest}
              >
                {formattedDate ?? null}
                <div className="absolute right-0 pr-1.5">{getIcon()}</div>
              </Button>
            </PopoverTrigger>
          </div>

          <PopoverContent className="w-auto p-0">
            <Calendar
              captionLayout="dropdown-buttons"
              mode="single"
              selected={
                value
                  ? // evito la conversione da utc a locale rimuovendo l'ora
                    new Date(dayjs.utc(value).format('YYYY-MM-DD'))
                  : undefined
              }
              onSelect={(date) => handleSelectDate(date)}
              initialFocus
              disabled={minDate ? (date) => date < minDate : undefined}
              locale={getUserLocale()}
            />
          </PopoverContent>
        </Popover>
      </div>

      <p className="h-4 text-xs ml-1 text-red overflow-hidden" id="error">
        {errorTranslated}
      </p>
    </div>
  );
};

export default DatePicker;
